/**
 * 코인 시세 위젯 (#406) — 회원이 코인/토큰을 검색해서 선택. 거래소별 실시간 비교.
 * - 검색 목록: 백엔드가 업비트(KRW)/빗썸(KRW)/바이낸스(USDT) 상장 코인을 합쳐 Firestore
 *   (trippang_widgets/crypto_markets)에 기록 → 클라이언트 onSnapshot (폴링 0). 검색 시에만 사용.
 * - 드롭다운 기본 목록: 회원이 지금까지 시세조회한 코인 이력(쿠키 crypto_history). 하드코딩 아님.
 *   이력이 없으면(한번도 변경 안함) 기본 USDT·BTC·ETH·XRP 표시. 마지막 조회 코인 = 이력 맨 앞(기본 USDT).
 * - 선택 코인 시세: 브라우저가 거래소 공개 WS 에 직접 연결(useCoinTicker, 폴링 0). 미상장 거래소 행은 무표시.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";
import { useCoinTicker } from "../../hooks/useCoinTicker";

interface Market { c: string; u: boolean; b: boolean; n: boolean; k?: string; e?: string; } // k=한글명, e=영문명
interface MarketsData { coins?: Market[]; updated_at?: string; }

// ── 쿠키 helper ──
function getCookie(name: string): string {
  if (typeof document === "undefined") return "";
  const m = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : "";
}
function setCookie(name: string, value: string, days = 365) {
  if (typeof document === "undefined") return;
  const exp = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${exp}; path=/; SameSite=Lax`;
}

const HISTORY_COOKIE = "crypto_history";
const DEFAULT_HISTORY = ["USDT", "BTC", "ETH", "XRP"]; // 한번도 변경 안한 회원의 기본 목록
const HISTORY_MAX = 20;

function readHistory(): string[] {
  const raw = getCookie(HISTORY_COOKIE);
  if (!raw) return [...DEFAULT_HISTORY];
  const arr = raw.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean);
  return arr.length ? arr : [...DEFAULT_HISTORY];
}

function ChangeBadge({ change }: { change?: number | null }) {
  if (change == null) return null;
  const pct = (change * 100).toFixed(2);
  const up = change > 0;
  const down = change < 0;
  return (
    <span className={`text-[10px] tabular-nums ${up ? "text-red-500" : down ? "text-blue-500" : "text-slate-400"}`}>
      {up ? "▲" : down ? "▼" : ""}{Math.abs(Number(pct))}%
    </span>
  );
}
function fmtKRW(v?: number | null) {
  if (v == null) return null;
  return "₩" + (v >= 100 ? Math.round(v).toLocaleString() : v.toLocaleString(undefined, { maximumFractionDigits: 4 }));
}
function fmtUSD(v?: number | null) {
  if (v == null) return null;
  return "$" + (v >= 1 ? v.toLocaleString(undefined, { maximumFractionDigits: 2 }) : v.toLocaleString(undefined, { maximumFractionDigits: 6 }));
}

export default function CryptoWidget() {
  const { t } = useTranslation();
  const [markets, setMarkets] = useState<Market[]>([]);
  const [history, setHistory] = useState<string[]>(readHistory);
  const [coin, setCoin] = useState<string>(() => readHistory()[0] || "USDT"); // 마지막 조회 코인(쿠키), 기본 USDT
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const boxRef = useRef<HTMLDivElement>(null);

  // 검색용 코인 목록 — Firestore onSnapshot (변동 적음, 폴링 0)
  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, "trippang_widgets", "crypto_markets"),
      (snap) => { if (snap.exists()) setMarkets(((snap.data() as MarketsData).coins) || []); },
      (err) => console.error("crypto markets snapshot:", err)
    );
    return () => unsub();
  }, []);

  // 드롭다운 외부 클릭 닫기 (리스너 정리 — 누수 방지)
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const avail = useMemo(() => markets.find((m) => m.c === coin) || null, [markets, coin]);
  const ticker = useCoinTicker(coin, avail);

  // 코인 심볼 → 한글명 맵 (목록 표시용)
  const nameOf = useMemo(() => {
    const map: Record<string, string> = {};
    for (const m of markets) if (m.k) map[m.c] = m.k;
    return map;
  }, [markets]);

  // 검색어 없으면 회원 조회 이력, 있으면 전체 유니버스 검색 (심볼·한글명·영문명)
  const list = useMemo<string[]>(() => {
    const raw = q.trim();
    if (!raw) return history;
    const su = raw.toUpperCase();
    return markets
      .filter((m) => m.c.includes(su) || (m.k && m.k.includes(raw)) || (m.e && m.e.toUpperCase().includes(su)))
      .map((m) => m.c)
      .slice(0, 60);
  }, [markets, q, history]);

  const selectCoin = (c: string) => {
    const next = [c, ...history.filter((x) => x !== c)].slice(0, HISTORY_MAX); // 맨 앞으로(중복 제거)
    setHistory(next);
    setCoin(c);
    setCookie(HISTORY_COOKIE, next.join(","));
    setOpen(false);
    setQ("");
  };

  const CRYPTO_ROW = "grid grid-cols-[1fr_auto_3.5rem] items-center gap-x-1.5";
  // 상장된 거래소만 행 표시 (미상장 = 무표시)
  const rows: Array<{ key: string; label: string; val: string | null; change: number | null }> = [];
  if (avail?.u) rows.push({ key: "upbit", label: t("widget.upbit"), val: fmtKRW(ticker.upbit?.price), change: ticker.upbit?.change ?? null });
  if (avail?.b) rows.push({ key: "bithumb", label: t("widget.bithumb"), val: fmtKRW(ticker.bithumb?.price), change: ticker.bithumb?.change ?? null });
  if (avail?.n) rows.push({ key: "binance", label: t("widget.binance"), val: fmtUSD(ticker.binance?.price), change: ticker.binance?.change ?? null });

  return (
    <div className="border-b border-slate-200 px-3 py-2.5">
      <div className="mb-1.5 flex items-center justify-between gap-1">
        <span className="text-xs font-semibold text-sky-600">{t("widget.cryptoTitle")}</span>
        <div className="relative" ref={boxRef}>
          <button
            onClick={() => setOpen((o) => !o)}
            className="flex items-center gap-1 rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[11px] font-semibold text-sky-600 hover:border-sky-400"
          >
            {coin}
            <span className="text-[8px] text-slate-400">▼</span>
          </button>
          {open && (
            <div className="absolute right-0 z-30 mt-1 w-44 rounded-lg border border-slate-200 bg-white shadow-xl">
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("widget.cryptoSearch")}
                className="w-full rounded-t-lg border-b border-slate-200 px-2 py-1.5 text-[11px] text-slate-900 outline-none"
              />
              <div className="max-h-52 overflow-y-auto overscroll-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {q && markets.length === 0 ? (
                  <div className="px-2 py-1 text-[11px] text-slate-400">{t("widget.loading")}</div>
                ) : list.length === 0 ? (
                  <div className="px-2 py-1 text-[11px] text-slate-400">{t("widget.cryptoNoResult")}</div>
                ) : (
                  list.map((c) => (
                    <button
                      key={c}
                      // 선택은 onClick(탭에서만 발화 — 스크롤 중엔 발화 안 함). onMouseDown preventDefault 로 blur 차단.
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => selectCoin(c)}
                      className={`flex w-full items-baseline gap-1.5 px-2 py-1 text-left text-[11px] hover:bg-sky-50 ${c === coin ? "font-bold text-sky-600" : "text-slate-700"}`}
                    >
                      <span className="shrink-0">{c}</span>
                      {nameOf[c] && <span className="truncate text-[10px] font-normal text-slate-400">{nameOf[c]}</span>}
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="py-1 text-[11px] text-slate-400">{t("widget.loading")}</div>
      ) : (
        <div className="flex flex-col gap-1">
          {rows.map((r) => (
            <div key={r.key} className={CRYPTO_ROW}>
              <span className="text-[11px] text-slate-600">{r.label}</span>
              <span className="text-right text-xs font-bold tabular-nums text-slate-900">{r.val ?? "…"}</span>
              <span className="text-right"><ChangeBadge change={r.change} /></span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
